import { toast } from 'react-toastify';
import { useEffect, useContext } from 'react';
import {
  Card,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  InputBase,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/system';
import { Formik } from 'formik';
import { registerSchema } from '../../../../schemas/auth.request';
import { useRegisterMutation } from '../../../../store/api/authApi';
import { useNavigate } from 'react-router-dom';
import GoogleLoginBtn from '../../../LoginPage/components/GoogleLoginBtn';
import { AppContext } from '../../../../context/ContextProvider';

const RegisterCardContainer = styled(Card)(({ theme }) => ({
  display: 'flex',
  width: '393px',
  height: '420px',
  margin: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(2px)',
  borderRadius: '0',
  [theme.breakpoints.down('600')]: {
    width: '363px',
  },
}));

const RegisterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '373px',
  height: '400px',
  padding: '30px',
  margin: 'auto',
  border: '1px solid #AA9178',
  gap: '25px',
  [theme.breakpoints.down('600')]: {
    width: '343px',
  },
}));

const RegisterTitleContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});
const RegisterTitle = styled(Typography)({
  fontFamily: 'Fjalla One',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '25px',
  alignItems: 'center',
  color: '#FFFFFF',
});

const AuthenticationInputLabel = styled(InputLabel)(() => ({
  color: '#FFFFFF',
  fontFamily: 'Raleway',
  fontSize: '16px',
  lineHeight: '16px',
}));

const AuthenticationBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '20px 0',
  height: '40px',
}));

const AuthenticationInput = styled(InputBase)(() => ({
  background: '#FFFFFF',
  border: '2px solid #AA9178',
  borderRadius: '5px',
}));

const RegisterOrGoogleButtonContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '12px 0 -6px',
}));

const RegisterButton = styled(Button)(() => ({
  width: '60px',
  height: '23px',
  textTransform: 'none',
  fontFamily: 'Fjalla One',
  fontSize: '14px',
  lineHeight: '18px',
  color: '#FFFFFF',
  backgroundColor: '#AA9178',
  borderRadius: '0',
}));

const ErrorFormHelperText = styled(FormHelperText)(() => ({
  fontFamily: 'Raleway',
}));

const validateSchema = (values) => {
  const errors = {};
  const valid = registerSchema.validate(values, { abortEarly: false });
  if (valid.error) {
    valid.error.details.forEach((err) => {
      errors[err.path.join('')] = err.message;
    });
  }
  return errors;
};

const RegisterCard = () => {
  const [
    register,
    {
      data: registerData,
      isSuccess: isRegisterSuccess,
      isError: isRegisterError,
      error: registerError,
    },
  ] = useRegisterMutation();
  const navigate = useNavigate();
  const { isShow, setIsShow, isLogin, setIsLogin } = useContext(AppContext);

  useEffect(() => {
    if (isRegisterSuccess) {
      navigate('/signin');
      toast.success('registered, please sign in.');
    }
    if (isRegisterError && registerError.status === 400) {
      toast.error('user already exist, please sign in.');
    }
  }, [isRegisterSuccess, isRegisterError]);

  const submitForm = async (data) => {
    if (data) {
      await register(data);
    } else {
      toast.error('please fill in all fields!');
    }
  };

  const handleLogin = () => {
    navigate('/search');
    setIsShow(true);
    setIsLogin(true);
  };

  return (
    <RegisterCardContainer>
      <RegisterContent>
        <RegisterTitleContainer>
          <RegisterTitle>Register your account</RegisterTitle>
        </RegisterTitleContainer>
        <Formik
          initialValues={{
            userName: '',
            email: '',
            password: '',
          }}
          validate={validateSchema}
          onSubmit={submitForm}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
            <form onSubmit={handleSubmit}>
              <FormControl variant="standard" sx={{ width: '100%' }}>
                <AuthenticationInputLabel
                  shrink
                  sx={{ '&.Mui-focused': { color: '#FFFFFF' } }}
                  htmlFor="userName"
                >
                  Account Name
                </AuthenticationInputLabel>
                <AuthenticationBox>
                  <AuthenticationInput
                    id="userName"
                    type="userName"
                    value={values.userName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.userName && errors.userName && (
                    <ErrorFormHelperText error>{errors.userName}</ErrorFormHelperText>
                  )}
                </AuthenticationBox>
              </FormControl>
              <FormControl variant="standard" sx={{ width: '100%' }}>
                <AuthenticationInputLabel
                  shrink
                  sx={{ '&.Mui-focused': { color: '#FFFFFF' } }}
                  htmlFor="email"
                >
                  Email
                </AuthenticationInputLabel>
                <AuthenticationBox>
                  <AuthenticationInput
                    id="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.email && errors.email && (
                    <ErrorFormHelperText error>{errors.email}</ErrorFormHelperText>
                  )}
                </AuthenticationBox>
              </FormControl>
              <FormControl variant="standard" sx={{ width: '100%' }}>
                <AuthenticationInputLabel
                  shrink
                  sx={{ '&.Mui-focused': { color: '#FFFFFF' } }}
                  htmlFor="password"
                >
                  Password
                </AuthenticationInputLabel>
                <AuthenticationBox>
                  <AuthenticationInput
                    id="password"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.password && errors.password && (
                    <ErrorFormHelperText error>{errors.password}</ErrorFormHelperText>
                  )}
                </AuthenticationBox>
              </FormControl>
              <RegisterOrGoogleButtonContainer>
                <RegisterButton type="submit">Register</RegisterButton>
                <GoogleLoginBtn pageLogin={true} handleLogin={handleLogin} />
              </RegisterOrGoogleButtonContainer>
            </form>
          )}
        </Formik>
      </RegisterContent>
    </RegisterCardContainer>
  );
};

export default RegisterCard;
